import { ref, shallowRef } from "vue";
import {
  fetchWithAuth,
  getApiOrigin,
  isJsonObject,
  type JsonObject,
} from "@blpde/connect";

/**
 * Appearances (I-2510; UI „Design"): named, shareable looks.
 * Master lists the built-in ones (the catalog schemes, id = catalog id) next
 * to the stored ones the session may see (id = number as a decimal string):
 * the tenant's, the company's and the account's personal ones. The active one
 * is the account config key `appearance` (`active`); its look is the `look`
 * layer of the appearance (`target=appearance:<id>`).
 */
export type AppearanceOwner = "platform" | "tenant" | "company" | "account";

export interface AppearanceEntry {
  /** Catalog id of a built-in, decimal id of a stored appearance. */
  id: string;
  name: string;
  builtin: boolean;
  owner: AppearanceOwner;
  /** The session may rename, delete and edit it. */
  editable: boolean;
}

/** Error codes of the appearances API. */
export const APPEARANCE_ERROR = {
  /** Personal appearances are switched off (`appearance.allow_personal`). */
  personalDisabled: "personal_appearances_disabled",
} as const;

export class AppearanceRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message || code || `HTTP ${status}`);
    this.name = "AppearanceRequestError";
    this.status = status;
    this.code = code;
  }
}

const OWNERS: readonly AppearanceOwner[] = [
  "platform",
  "tenant",
  "company",
  "account",
];

const endpoint = (path = ""): string =>
  `${getApiOrigin()}/v1/appearances${path}`;

/** Reads one appearance leniently; null when it is not one. */
export function parseAppearanceEntry(raw: unknown): AppearanceEntry | null {
  if (!isJsonObject(raw)) return null;
  const id =
    typeof raw.id === "number" || typeof raw.id === "string"
      ? String(raw.id).trim()
      : "";
  if (!id) return null;
  const builtin = raw.builtin === true;
  const owner = OWNERS.includes(raw.owner as AppearanceOwner)
    ? (raw.owner as AppearanceOwner)
    : builtin
      ? "platform"
      : "account";
  return {
    id,
    name: typeof raw.name === "string" ? raw.name : id,
    builtin,
    owner,
    editable: !builtin && raw.editable === true,
  };
}

async function expectOk(response: Response): Promise<Response> {
  if (response.ok) return response;
  let code = "";
  let message = "";
  try {
    const body = (await response.json()) as {
      code?: unknown;
      message?: unknown;
    };
    if (typeof body?.code === "string") code = body.code;
    if (typeof body?.message === "string") message = body.message;
  } catch {
    /* no JSON body */
  }
  throw new AppearanceRequestError(response.status, code, message);
}

function listOf(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isJsonObject(body) && Array.isArray(body.appearances))
    return body.appearances;
  return [];
}

function entryOf(body: unknown): AppearanceEntry {
  const entry = parseAppearanceEntry(
    isJsonObject(body) && isJsonObject(body.appearance)
      ? body.appearance
      : body,
  );
  if (!entry)
    throw new AppearanceRequestError(
      200,
      "invalid_response",
      "invalid appearance response",
    );
  return entry;
}

function jsonRequest(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

/** Built-ins and the stored appearances the session may see (`GET /v1/appearances`). */
export async function fetchAppearances(): Promise<AppearanceEntry[]> {
  const response = await expectOk(
    await fetchWithAuth(endpoint(), { cache: "no-store" }),
  );
  const out: AppearanceEntry[] = [];
  for (const raw of listOf(await response.json().catch(() => null))) {
    const entry = parseAppearanceEntry(raw);
    if (entry && !out.some((item) => item.id === entry.id)) out.push(entry);
  }
  return out;
}

export interface CreateAppearanceInput {
  name: string;
  /** Who owns it; company and tenant need the admin rights. Default: the account (personal). */
  owner?: Exclude<AppearanceOwner, "platform">;
  /** Copy the look of this appearance (built-in or stored) into the new one. */
  copyFrom?: string;
}

/** `POST /v1/appearances`; refused with `personal_appearances_disabled` when personal ones are off. */
export async function createAppearance(
  input: CreateAppearanceInput,
): Promise<AppearanceEntry> {
  const body: Record<string, unknown> = {
    name: input.name,
    owner: input.owner ?? "account",
  };
  if (input.copyFrom) body.copy_from = input.copyFrom;
  const response = await expectOk(
    await fetchWithAuth(endpoint(), jsonRequest("POST", body)),
  );
  return entryOf(await response.json().catch(() => null));
}

/** `PATCH /v1/appearances/:id` (the name). */
export async function renameAppearance(
  id: string,
  name: string,
): Promise<void> {
  await expectOk(
    await fetchWithAuth(
      endpoint(`/${encodeURIComponent(id)}`),
      jsonRequest("PATCH", { name }),
    ),
  );
}

/** `DELETE /v1/appearances/:id` — master drops its config rows with it. */
export async function deleteAppearance(id: string): Promise<void> {
  await expectOk(
    await fetchWithAuth(endpoint(`/${encodeURIComponent(id)}`), {
      method: "DELETE",
    }),
  );
}

/** One key an appearance sets: its layer's document and version (0 for a built-in's). */
export interface AppearanceConfig {
  key: string;
  data: JsonObject;
  version: number;
}

/** Everything an appearance sets (`GET /v1/appearances/:id/configs`). */
export interface AppearanceConfigs {
  appearance: string;
  /** The session may write it (`PUT`). */
  editable: boolean;
  /** Sorted by key. */
  configs: AppearanceConfig[];
}

function configsOf(body: unknown, id: string): AppearanceConfigs {
  if (!isJsonObject(body) || !Array.isArray(body.configs)) {
    throw new AppearanceRequestError(
      200,
      "invalid_response",
      "invalid appearance configs response",
    );
  }
  const configs: AppearanceConfig[] = [];
  for (const raw of body.configs) {
    if (!isJsonObject(raw) || typeof raw.key !== "string" || !raw.key) continue;
    configs.push({
      key: raw.key,
      data: isJsonObject(raw.data) ? (raw.data as JsonObject) : {},
      version: typeof raw.version === "number" ? raw.version : 0,
    });
  }
  configs.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return {
    appearance: typeof body.appearance === "string" ? body.appearance : id,
    editable: body.editable === true,
    configs,
  };
}

/** Every key an appearance sets — a built-in's too (`GET /v1/appearances/:id/configs`). */
export async function fetchAppearanceConfigs(
  id: string,
): Promise<AppearanceConfigs> {
  const response = await expectOk(
    await fetchWithAuth(endpoint(`/${encodeURIComponent(id)}/configs`), {
      cache: "no-store",
    }),
  );
  return configsOf(await response.json().catch(() => null), id);
}

/**
 * Replaces the whole content of a stored appearance, a document per key — a
 * key left out is emptied (`PUT /v1/appearances/:id/configs`). Master checks
 * every key first and writes nothing unless all pass.
 */
export async function replaceAppearanceConfigs(
  id: string,
  configs: Readonly<Record<string, JsonObject>>,
): Promise<AppearanceConfigs> {
  const response = await expectOk(
    await fetchWithAuth(
      endpoint(`/${encodeURIComponent(id)}/configs`),
      jsonRequest("PUT", { configs }),
    ),
  );
  return configsOf(await response.json().catch(() => null), id);
}

/* ── Shared list (Darstellung + look designer) ─────────────────────────── */

const appearances = shallowRef<AppearanceEntry[]>([]);
const state = ref<"idle" | "loading" | "ready" | "error">("idle");
let loading: Promise<void> | null = null;

async function load(): Promise<void> {
  if (loading) return loading;
  if (state.value !== "ready") state.value = "loading";
  loading = fetchAppearances()
    .then((list) => {
      appearances.value = list;
      state.value = "ready";
    })
    .catch(() => {
      state.value = "error";
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

/** The appearance list both the „Darstellung" topic and the look designer show. */
export function useAppearances() {
  return { appearances, state, load };
}

/** Test seam. */
export function resetAppearancesForTests(): void {
  appearances.value = [];
  state.value = "idle";
  loading = null;
}
